import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { isGroupOption } from "PFApp/booking/components/booking_form/workforce_member_select/workforce_member_select.utils";
import { Alert } from "PFComponents/alert";
import { LoadingDots } from "PFComponents/loading_dots/loading_dots";
import { Typography } from "PFComponents/typography";
import { Booking } from "PFTypes";
import React, { Dispatch, RefObject, SetStateAction, useEffect, useRef } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Carousel } from "../../carousel";
import { BookingDataItem, BookingFormData, BookingFormMode } from "../booking_form.types";
import { isEditMode } from "../booking_form.utils";
import { BookingFormValues } from "../booking_form_provider";
import { BookingType } from "../booking_type";
import { isSomeRequirementInvalid } from "../requirement_selector";
import { useValidateBookingsCategory } from "../use_validate_bookings_category";
import { BookingItem } from "./booking_item";
import css from "./booking_range_column.module.scss";
import { useDefaultBookingsMapper } from "./use_default_bookings_mapper";
import { useInitialBookings } from "./use_initial_bookings";

type BookingRangeColumnProps = {
  initialData: BookingFormData;
  mode: BookingFormMode;
  activeBookingIndex: number | undefined;
  setActiveBookingIndex: (index: number | undefined) => void;
  initialBooking?: Booking;
  errors?: Record<string, string>;
  loadingState: Record<string, boolean>;
  setLoadingState: Dispatch<SetStateAction<Record<string, boolean>>>;
  initialBookingsRef: RefObject<BookingDataItem[] | null>;
};

export const BookingRangeColumn = ({
  initialData,
  mode,
  initialBooking,
  activeBookingIndex,
  setActiveBookingIndex,
  loadingState,
  setLoadingState,
  initialBookingsRef
}: BookingRangeColumnProps) => {
  const { t } = useTranslation("bookingModule");
  const portalRef = useRef(null);
  const [bookingType, workforceMember] = useWatch<BookingFormValues, ["bookingType", "workforceMember"]>({
    name: ["bookingType", "workforceMember"]
  });

  const {
    fields: bookings,
    append,
    remove
  } = useFieldArray<BookingFormValues, "bookings", "key">({
    name: "bookings",
    keyName: "key",
    rules: {
      validate: (items: BookingDataItem[]) => !isEmpty(items) && !isSomeRequirementInvalid(items)
    }
  });

  useValidateBookingsCategory(mode);

  const { loading: areInitialBookingsLoading } = useInitialBookings({
    initialData,
    mode,
    booking: initialBooking,
    loadingState,
    setLoadingState,
    initialBookingsRef
  });

  const { getDefaultBooking } = useDefaultBookingsMapper({});

  const initialActiveIndex = Math.max(
    bookings.findIndex(({ id }) => id === initialBooking?.id),
    0
  );

  useEffect(() => {
    setActiveBookingIndex(initialActiveIndex);
  }, [initialActiveIndex]);

  useEffect(() => {
    if (!isNil(activeBookingIndex) && activeBookingIndex > bookings?.length - 1) {
      setActiveBookingIndex(initialActiveIndex);
    }
  }, [bookings]);

  if (areInitialBookingsLoading) {
    return <LoadingDots className={css.loading} />;
  }

  const addBooking = () => {
    append(getDefaultBooking());
  };

  const removeBooking = (index: number) => {
    remove(index);
  };

  const items = bookings.map(({ key }, itemIndex) => (
    <BookingItem key={key} itemIndex={itemIndex} portalRef={portalRef} mode={mode} />
  ));

  const isInEditMode = isEditMode(mode);
  const isRepeated = bookingType === BookingType.Repeated;
  const isGroupBookingSelected = isGroupOption(workforceMember);

  const header = (
    <Typography variant="bodyRegular">
      {isInEditMode && isRepeated
        ? t("bookings.repeatedBooking")
        : items.length > 1
          ? t("bookings.bookingOrdinalNumber", {
              current: (activeBookingIndex || 0) + 1,
              total: items.length
            })
          : t("bookings.bookingOrdinalNumberSingle", {
              current: (activeBookingIndex || 0) + 1
            })}
    </Typography>
  );

  return (
    <>
      {!!isGroupBookingSelected && <Alert title={t("details.groupBookingRangesWarning")} multiLineTitle />}
      <section id="calendar_portal">
        <Carousel
          items={items}
          header={mode !== BookingFormMode.EditSingle ? header : null}
          onAdd={!isInEditMode ? addBooking : undefined}
          onDelete={!isInEditMode ? removeBooking : undefined}
          activeIndex={activeBookingIndex || 0}
          setActiveIndex={setActiveBookingIndex}
        />
        <div ref={portalRef} />
      </section>
    </>
  );
};
